export interface EmotionEntry {
  id: string;
  emotion: string;
  reason: string;
  date: string;
}

export interface DrawingEntry {
  id: string;
  emotion: string;
  imageData: string;
  date: string;
}

interface GratitudeEntry {
  id: string;
  happy: string;
  person: string;
  fun: string;
  date: string;
}

interface GoalEntry {
  id: string;
  label: string;
  option: string;
  completed: boolean;
  date: string;
}

class StorageService {
  private readonly EMOTIONS_KEY = 'calmi_emotions';
  private readonly GRATITUDES_KEY = 'calmi_gratitudes';
  private readonly GOALS_KEY = 'calmi_goals';
  private readonly DRAWINGS_KEY = 'calmi_drawings';
  private readonly STORIES_KEY = 'calmi_stories';
  private readonly THOUGHTS_KEY = 'calmi_thoughts';

  // Emotions
  saveEmotion(emotion: string, reason: string): void {
    const emotions = this.getEmotions();
    const entry: EmotionEntry = {
      id: Date.now().toString(),
      emotion,
      reason,
      date: new Date().toISOString()
    };
    emotions.push(entry);
    localStorage.setItem(this.EMOTIONS_KEY, JSON.stringify(emotions));
  }

  getEmotions(): EmotionEntry[] {
    const data = localStorage.getItem(this.EMOTIONS_KEY);
    return data ? JSON.parse(data) : [];
  }

  getEmotionsByPeriod(days: number): EmotionEntry[] {
    const emotions = this.getEmotions();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return emotions.filter(e => new Date(e.date) >= cutoffDate);
  }

  // Gratitudes
  saveGratitude(happy: string, person: string, fun: string): void {
    const gratitudes = this.getGratitudes();
    const entry: GratitudeEntry = {
      id: Date.now().toString(),
      happy,
      person,
      fun,
      date: new Date().toISOString()
    };
    gratitudes.push(entry);
    localStorage.setItem(this.GRATITUDES_KEY, JSON.stringify(gratitudes));
  }

  getGratitudes(): GratitudeEntry[] {
    const data = localStorage.getItem(this.GRATITUDES_KEY);
    return data ? JSON.parse(data) : [];
  }

  getGratitudesByPeriod(days: number): GratitudeEntry[] {
    const gratitudes = this.getGratitudes();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return gratitudes.filter(g => new Date(g.date) >= cutoffDate);
  }

  // Goals
  saveGoal(label: string, option: string, completed: boolean = false): void {
    const goals = this.getGoals();
    const entry: GoalEntry = {
      id: Date.now().toString(),
      label,
      option,
      completed,
      date: new Date().toISOString()
    };
    goals.push(entry);
    localStorage.setItem(this.GOALS_KEY, JSON.stringify(goals));
  }

  updateGoalCompletion(goalId: string, completed: boolean): void {
    const goals = this.getGoals();
    const index = goals.findIndex(g => g.id === goalId);
    if (index !== -1) {
      goals[index].completed = completed;
      localStorage.setItem(this.GOALS_KEY, JSON.stringify(goals));
    }
  }

  getGoals(): GoalEntry[] {
    const data = localStorage.getItem(this.GOALS_KEY);
    return data ? JSON.parse(data) : [];
  }

  getGoalsByPeriod(days: number): GoalEntry[] {
    const goals = this.getGoals();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return goals.filter(g => new Date(g.date) >= cutoffDate);
  }

  getCompletedGoals(): GoalEntry[] {
    return this.getGoals().filter(g => g.completed);
  }

  // Statistics
  getEmotionStats(days: number): { [key: string]: number } {
    const emotions = this.getEmotionsByPeriod(days);
    const stats: { [key: string]: number } = {};
    emotions.forEach(e => {
      stats[e.emotion] = (stats[e.emotion] || 0) + 1;
    });
    return stats;
  }

  getStreak(): number {
    const goals = this.getGoals();
    const completedGoals = goals.filter(g => g.completed);
    if (completedGoals.length === 0) return 0;

    const dates = Array.from(new Set(completedGoals.map(g => g.date.split('T')[0])));
    let streak = 0;
    let currentDate = new Date();
    
    for (let i = 0; i < 365; i++) {
      const dateStr = currentDate.toISOString().split('T')[0];
      if (dates.indexOf(dateStr) !== -1) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }
    
    return streak;
  }

  clearAll(): void {
    localStorage.removeItem(this.EMOTIONS_KEY);
  }

  // Drawings
  saveDrawing(emotion: string, imageData: string): void {
    const drawings = this.getDrawings();
    const entry: DrawingEntry = {
      id: Date.now().toString(),
      emotion,
      imageData,
      date: new Date().toISOString()
    };
    drawings.push(entry);
    localStorage.setItem(this.DRAWINGS_KEY, JSON.stringify(drawings));
  }

  getDrawings(): DrawingEntry[] {
    const data = localStorage.getItem(this.DRAWINGS_KEY);
    return data ? JSON.parse(data) : [];
  }

  // Stories
  saveStory(story: string): void {
    const stories = this.getStories();
    const entry = {
      id: Date.now().toString(),
      story,
      date: new Date().toISOString()
    };
    stories.push(entry);
    localStorage.setItem(this.STORIES_KEY, JSON.stringify(stories));
  }

  getStories(): any[] {
    const data = localStorage.getItem(this.STORIES_KEY);
    return data ? JSON.parse(data) : [];
  }

  // Thoughts
  saveThought(thought: string): void {
    const thoughts = this.getThoughts();
    const entry = {
      id: Date.now().toString(),
      thought,
      date: new Date().toISOString()
    };
    thoughts.push(entry);
    localStorage.setItem(this.THOUGHTS_KEY, JSON.stringify(thoughts));
  }

  getThoughts(): any[] {
    const data = localStorage.getItem(this.THOUGHTS_KEY);
    return data ? JSON.parse(data) : [];
  }
}

export const storageService = new StorageService();
